import { IonButton, IonIcon, IonPopover } from '@ionic/react';
import { helpCircle } from 'ionicons/icons';
import React, { useState } from 'react';
import { useBreakpoint } from '../../../context/breakpoint/BreakpointContext';
import { iTrack } from '../../../types/ITrack';
import { useLoading } from '../../RootLoader/useLoading';
import PostHogService from '../../../services/PostHog.service';
import { config } from '../../../config';
import isElectron from '../../../utils/isElectron';
import { findBestMixableSequence } from '../../../utils/magicSort';
import style from './style.module.scss';

interface MagicSorterProps {
  tracks: iTrack[];
  linkedTracks: iTrack[][];
  onSortChange: (sortedTracks: iTrack[]) => void;
  isSortable?: boolean;
}

const MagicSorter: React.FC<MagicSorterProps> = ({
  tracks,
  linkedTracks,
  onSortChange,
  isSortable = true,
}) => {
  const { matchesMediumDown } = useBreakpoint();
  const [sorting, setSorting] = useState(false);
  const { setIsLoading } = useLoading();

  // Memoized sorted tracks
  const sortTracks = async () => {
    const startTime = performance.now();
    setSorting(true);
    setIsLoading(true);

    try {
      const normalizeTrackIds = (track: any) => ({
        ...track,
        ID: track.ID || track._id || track.SpotifyID,
      });
      const cleanedTracks = tracks.map(normalizeTrackIds);
      const newSequence = findBestMixableSequence(cleanedTracks);
      const normalizedSequence = newSequence.map(normalizeTrackIds);
      const orderChanged = normalizedSequence.some(
        (t, i) => t.ID !== tracks[i]?.ID,
      );

      const endTime = performance.now();
      const sortDuration = endTime - startTime;

      const tracksWithBPM = cleanedTracks.filter((t) => t.BPM).length;
      const tracksWithKey = cleanedTracks.filter((t) => t.Key_Camelot).length;
      const tracksWithBoth = cleanedTracks.filter(
        (t) => t.BPM && t.Key_Camelot,
      ).length;

      try {
        if (PostHogService.isInitialized()) {
          PostHogService.capture('magic_sort_executed', {
            track_count: cleanedTracks.length,
            tracks_with_bpm: tracksWithBPM,
            tracks_with_key: tracksWithKey,
            tracks_with_both: tracksWithBoth,
            order_changed: orderChanged,
            sort_duration_ms: Math.round(sortDuration),
            is_sortable: isSortable,
            is_electron: isElectron(),
            app_version: config.VERSION,
          });
        }
      } catch (error) {
        console.error('Error capturing PostHog magic sort event:', error);
      }

      if (orderChanged) {
        onSortChange(normalizedSequence);
      }
    } catch (error) {
      try {
        if (PostHogService.isInitialized()) {
          PostHogService.capture('magic_sort_failed', {
            track_count: tracks.length,
            error_message:
              error instanceof Error ? error.message : 'Unknown error',
            is_electron: isElectron(),
            app_version: config.VERSION,
          });
        }
      } catch (err) {
        console.error('Error capturing PostHog magic sort failure event:', err);
      }
      throw error;
    } finally {
      setSorting(false);
      setIsLoading(false);
    }
  };

  return (
    <div className={style['sort-button-container']}>
      <IonButton
        className={`${style['sort-button']}`}
        size={matchesMediumDown ? 'small' : 'default'}
        disabled={sorting || !isSortable}
        onClick={sortTracks}
        id="magic-sort-button"
      >
        {sorting ? `Sorting tracks...` : `Magic Sort`}
      </IonButton>
      {!isSortable && (
        <>
          <IonIcon
            icon={helpCircle}
            className={style['help-icon']}
            id="magic-sort-help"
          />
          <IonPopover
            trigger="magic-sort-help"
            dismissOnSelect={true}
            side="top"
            alignment="center"
          >
            Tracks must have BPM and Key to use Magic Sort
          </IonPopover>
        </>
      )}
    </div>
  );
};

export default MagicSorter;
